// V17.266: every user-facing string on the AR Residential team board.
//
// One module for the TV, the shared table, and the in-app page so a label can
// never say one thing on the wall display and another on someone's laptop —
// the same reason TeamBoardTable itself is shared.
//
// Lao is the app-wide default (LanguageContext defaults to 'lo'). These Lao
// strings were drafted by Claude and reviewed by Vongphet before merge; the
// month-to-date terms in particular were the least certain.

import { useLanguage } from '../contexts/LanguageContext';

/**
 * Task 2026-10-26-002: the group name the API sends for officers with no team.
 * MUST equal backend `UNASSIGNED_TEAM` (`backend/app/services/residential_team_board.py`);
 * the API, the Excel export and the sort key keep the English value — only the
 * header shown on screen is translated (`TeamBoardStrings.unassigned`).
 */
export const UNASSIGNED_TEAM = 'Unassigned';

export interface TeamBoardColumns {
  no: string;
  teamStaff: string;
  callTarget: string;
  actualCalls: string;
  callPct: string;
  callsRemaining: string;
  collectionTarget: string;
  actualCollection: string;
  collectionPct: string;
  collectionRemaining: string;
  monthTarget: string;
  mtdActual: string;
  mtdPct: string;
  mtdRemaining: string;
}

export interface TeamBoardStrings {
  // TV chrome
  title: string;
  subtitle: string;
  live: string;
  stale: string;
  updated: (time: string) => string;
  nextRefresh: (seconds: number) => string;
  leaders: string;
  disconnected: string;
  rePair: string;
  loading: string;
  // V17.348 — on-screen dark-mode control on the wall display
  switchToDark: string;
  switchToLight: string;
  summary: (officers: number, teams: number) => string;
  page: (n: number, total: number) => string;
  autoRefresh: string;
  rotates: string;
  // Shared table
  columns: TeamBoardColumns;
  teamTotal: string;
  grandTotal: string;
  /** Header of the no-team group (API value `UNASSIGNED_TEAM`). */
  unassigned: string;
  // In-app page only (D7)
  asOf: (date: string) => string;
  refresh: string;
  tvMode: string;
  exportExcel: string;
  exportFailed: string;
  refreshFailed: string;
  // V17.299 — commercial board
  commercialTitle: string;
  commercialSubtitle: string;
  commercialRePair: string;
  // V17.359 — freshness of the Legacy billing delta sync feeding the cash
  // columns
  legacySynced: (time: string) => string;
  notRecorded: {
    calls: string;
    collections: string;
    ledger: string;
  };
}

/** Table column order. TeamBoardTable maps over this, so it is the ordering. */
export const COLUMN_KEYS = [
  'no', 'teamStaff', 'callTarget', 'actualCalls', 'callPct', 'callsRemaining',
  'collectionTarget', 'actualCollection', 'collectionPct', 'collectionRemaining',
  'monthTarget', 'mtdActual', 'mtdPct', 'mtdRemaining',
] as const satisfies readonly (keyof TeamBoardColumns)[];

const en: TeamBoardStrings = {
  title: 'Daily Dashboard — AR Residential',
  subtitle: 'cash collected today · LAK',
  live: '● LIVE',
  stale: '● STALE',
  updated: (time) => `updated ${time}`,
  nextRefresh: (seconds) => `next update in ${seconds}s`,
  leaders: "Today's Leaders",
  disconnected: 'DISPLAY DISCONNECTED',
  rePair: 'Re-pair this TV from the AR Residential Team Board page.',
  loading: 'Loading…',
  switchToDark: 'Switch to dark mode',
  switchToLight: 'Switch to light mode',
  summary: (officers, teams) => `${officers} officers · ${teams} teams`,
  page: (n, total) => `page ${n} of ${total}`,
  autoRefresh: 'auto-refresh 30s',
  rotates: 'rotates every 15s',
  columns: {
    no: 'No',
    teamStaff: 'Team / Staff',
    callTarget: 'Call Target',
    actualCalls: 'Actual Calls',
    callPct: 'Call %',
    callsRemaining: 'Calls Remaining',
    collectionTarget: 'Collection Target',
    actualCollection: 'Actual Collection',
    collectionPct: 'Collection %',
    collectionRemaining: 'Collection Remaining',
    monthTarget: 'Month Target',
    mtdActual: 'MTD Actual',
    mtdPct: 'MTD %',
    mtdRemaining: 'Month Remaining',
  },
  teamTotal: 'Team total',
  grandTotal: 'Grand Total',
  unassigned: 'Unassigned',
  asOf: (date) => `As of ${date}`,
  refresh: '↻ Refresh',
  tvMode: '⛶ TV Mode',
  exportExcel: '⬇ Export Excel',
  exportFailed: 'Could not export the board.',
  refreshFailed: 'Could not refresh the board.',
  commercialTitle: 'Daily Dashboard — AR Commercial',
  commercialSubtitle: 'cash collected today · LAK',
  commercialRePair: 'Re-pair this TV from the AR Commercial Team Board page.',
  legacySynced: (time) => `Legacy billing synced ${time}`,
  // V17.359: the commercial cash columns read Legacy billing payments by
  // portfolio — officers logging activity is no longer what fills them in.
  notRecorded: {
    calls:
      'No calls or site visits have been recorded in the last 90 days. '
      + 'These columns will fill in once officers log their activity.',
    collections:
      'No paid commercial bills have arrived from Legacy billing in the '
      + 'last 90 days. These columns fill in automatically once the Legacy '
      + 'sync brings payments over.',
    ledger:
      'No paid commercial bills have arrived from Legacy billing in the '
      + 'last 90 days. The month-to-date columns fill in automatically once '
      + 'the Legacy sync brings payments over.',
  },
};

const lo: TeamBoardStrings = {
  title: 'ກະດານປະຈຳວັນ — AR ສາຍບ້ານ',
  subtitle: 'ເງິນສົດທີ່ເກັບໄດ້ມື້ນີ້ · ກີບ',
  live: '● ສົດ',
  stale: '● ຂໍ້ມູນເກົ່າ',
  updated: (time) => `ອັບເດດ ${time}`,
  nextRefresh: (seconds) => `ອັບເດດຄັ້ງຕໍ່ໄປໃນ ${seconds} ວິນາທີ`,
  leaders: 'ຜູ້ຍອດສູງສຸດ',
  disconnected: 'ຈໍສະແດງຜົນຂາດການເຊື່ອມຕໍ່',
  rePair: 'ກະລຸນາຈັບຄູ່ໂທລະທັດນີ້ຄືນຈາກໜ້າກະດານທີມ AR ສາຍບ້ານ',
  loading: 'ກຳລັງໂຫຼດ…',
  switchToDark: 'ປ່ຽນເປັນໂໝດມືດ',
  switchToLight: 'ປ່ຽນເປັນໂໝດແຈ້ງ',
  summary: (officers, teams) => `ພະນັກງານ ${officers} ຄົນ · ${teams} ທີມ`,
  page: (n, total) => `ໜ້າ ${n} ຈາກ ${total}`,
  autoRefresh: 'ໂຫຼດຄືນອັດຕະໂນມັດ 30 ວິນາທີ',
  rotates: 'ປ່ຽນໜ້າທຸກ 15 ວິນາທີ',
  columns: {
    no: 'ລຳດັບ',
    teamStaff: 'ທີມ / ພະນັກງານ',
    callTarget: 'ເປົ້າໝາຍໂທ',
    actualCalls: 'ໂທຕົວຈິງ',
    callPct: '% ໂທ',
    callsRemaining: 'ຍັງເຫຼືອໂທ',
    collectionTarget: 'ເປົ້າໝາຍເກັບເງິນ',
    actualCollection: 'ເກັບໄດ້ຕົວຈິງ',
    collectionPct: '% ເກັບເງິນ',
    collectionRemaining: 'ຍັງເຫຼືອເກັບເງິນ',
    monthTarget: 'ເປົ້າໝາຍເດືອນ',
    mtdActual: 'ສະສົມເດືອນນີ້',
    mtdPct: '% ສະສົມເດືອນນີ້',
    mtdRemaining: 'ຍັງເຫຼືອເດືອນນີ້',
  },
  teamTotal: 'ລວມທີມ',
  grandTotal: 'ລວມທັງໝົດ',
  // Same wording as My Team (ARResidentialMyTeam.tsx).
  unassigned: 'ຍັງບໍ່ໄດ້ຈັດກຸ່ມ',
  asOf: (date) => `ຂໍ້ມູນວັນທີ ${date}`,
  refresh: '↻ ໂຫຼດຄືນ',
  tvMode: '⛶ ໂໝດໂທລະທັດ',
  exportExcel: '⬇ ດາວໂຫຼດ Excel',
  exportFailed: 'ບໍ່ສາມາດດາວໂຫຼດ Excel ໄດ້',
  refreshFailed: 'ບໍ່ສາມາດໂຫຼດຂໍ້ມູນຄືນໄດ້',
  // V17.299 — commercial board. Drafted by Claude; flagged for Vongphet's
  // review before merge, matching the V17.266 note above.
  commercialTitle: 'ກະດານປະຈຳວັນ — AR ທຸລະກິດ',
  commercialSubtitle: 'ເງິນສົດທີ່ເກັບໄດ້ມື້ນີ້ · ກີບ',
  commercialRePair: 'ກະລຸນາຈັບຄູ່ໂທລະທັດນີ້ຄືນຈາກໜ້າກະດານທີມ AR ທຸລະກິດ',
  legacySynced: (time) => `ຊິງຂໍ້ມູນບິນຈາກ Legacy ${time}`,
  // V17.359: ຖັນເງິນອ່ານຈາກການຊຳລະບິນ Legacy ຕາມພອດໂຟລິໂອ —
  // ບໍ່ຕ້ອງລໍຖ້າພະນັກງານບັນທຶກອີກຕໍ່ໄປ. Drafted by Claude; flagged for
  // Vongphet's review, matching the V17.299 note above.
  notRecorded: {
    calls:
      'ບໍ່ມີການບັນທຶກການໂທ ຫຼື ການລົງພື້ນທີ່ໃນ 90 ວັນຜ່ານມາ '
      + 'ຖັນເຫຼົ່ານີ້ຈະມີຂໍ້ມູນເມື່ອພະນັກງານບັນທຶກກິດຈະກຳ',
    collections:
      'ຍັງບໍ່ມີບິນທຸລະກິດທີ່ຊຳລະແລ້ວເຂົ້າມາຈາກລະບົບ Legacy ໃນ 90 ວັນຜ່ານມາ '
      + 'ຖັນເຫຼົ່ານີ້ຈະມີຂໍ້ມູນອັດຕະໂນມັດເມື່ອການຊິງດຶງການຊຳລະເຂົ້າມາ',
    ledger:
      'ຍັງບໍ່ມີບິນທຸລະກິດທີ່ຊຳລະແລ້ວເຂົ້າມາຈາກລະບົບ Legacy ໃນ 90 ວັນຜ່ານມາ '
      + 'ຖັນສະສົມເດືອນນີ້ຈະມີຂໍ້ມູນອັດຕະໂນມັດເມື່ອການຊິງດຶງການຊຳລະເຂົ້າມາ',
  },
};

export const TEAM_BOARD_STRINGS: Record<'lo' | 'en', TeamBoardStrings> = { lo, en };

/** Strings for the active language. Falls back to Lao, the app-wide default. */
export function useTeamBoardStrings(): TeamBoardStrings {
  const { language } = useLanguage();
  return TEAM_BOARD_STRINGS[language] ?? TEAM_BOARD_STRINGS.lo;
}
